// apps/web/src/pages/maintenance/ChecklistsPage.tsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, ClipboardList, ChevronRight, X, Trash2, ToggleLeft, ToggleRight } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

// ---- Types ----
interface Company { id: string; name: string }
interface Site { id: string; name: string }
interface Asset { id: string; name: string; assetType: string | null }
interface Template {
  id: string; name: string; description: string | null
  customerId: string | null; siteId: string | null
  frequency: string
  operations: { id: string }[]
  createdAt: string
}
interface ControlPoint {
  assetId: string; title: string; description: string; requiresPhoto: boolean; expectedOutcome: string
}

const FREQ_LABELS: Record<string, string> = {
  once: 'Una tantum', daily: 'Giornaliera', weekly: 'Settimanale',
  monthly: 'Mensile', quarterly: 'Trimestrale', semestrale: 'Semestrale', yearly: 'Annuale',
}

const emptyPoint: ControlPoint = { assetId: '', title: '', description: '', requiresPhoto: false, expectedOutcome: '' }

// ---- Modal ----
function NewTemplateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [info, setInfo] = useState({ name: '', description: '', customerId: '', siteId: '', frequency: 'monthly' })
  const [points, setPoints] = useState<ControlPoint[]>([])
  const [draft, setDraft] = useState<ControlPoint>(emptyPoint)

  const { data: companies = [] } = useQuery({
    queryKey: ['companies', 'list'],
    queryFn: async () => (await api.get<Company[]>('/companies')).data,
  })
  const { data: sites = [] } = useQuery({
    queryKey: ['sites', info.customerId],
    queryFn: async () => (await api.get<Site[]>('/sites', { params: { companyId: info.customerId } })).data,
    enabled: Boolean(info.customerId),
  })
  const { data: assets = [] } = useQuery({
    queryKey: ['assets', 'hardware', info.customerId],
    queryFn: async () => {
      const params: Record<string, string> = { category: 'hardware' }
      if (info.customerId) params['companyId'] = info.customerId
      return (await api.get<Asset[]>('/assets', { params })).data
    },
    enabled: Boolean(info.customerId),
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      const tpl = await api.post<{ id: string }>('/checklist-templates', {
        name: info.name, description: info.description || undefined,
        customerId: info.customerId || undefined,
        siteId: info.siteId || undefined,
        frequency: info.frequency,
      })
      await Promise.all(points.map((p, idx) =>
        api.post(`/checklist-templates/${tpl.data.id}/operations`, {
          ...p,
          assetId: p.assetId || undefined,
          expectedOutcome: p.expectedOutcome || undefined,
          description: p.description || undefined,
          stepOrder: idx,
        })
      ))
      return tpl.data.id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] })
      onClose()
    },
  })

  function addPoint() {
    if (!draft.title) return
    setPoints(p => [...p, { ...draft }])
    setDraft(emptyPoint)
  }

  const selectedAsset = assets.find(a => a.id === draft.assetId)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-12 overflow-y-auto">
      <div className="relative w-full max-w-2xl rounded-2xl bg-[var(--fc-surface)] shadow-2xl mx-4 mb-12">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--fc-border)] px-6 py-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-[var(--fc-primary)]" />
            <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Nuovo Template Checklist</h2>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Row 1: Nome + Cliente */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Nome Template *</label>
              <input
                value={info.name}
                onChange={e => setInfo(p => ({ ...p, name: e.target.value }))}
                placeholder="Es: Manutenzione trimestrale"
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Cliente</label>
              <select
                value={info.customerId}
                onChange={e => setInfo(p => ({ ...p, customerId: e.target.value, siteId: '' }))}
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
              >
                <option value="">— tutti i clienti —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Descrizione + Frequenza + Sito */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Descrizione</label>
              <textarea
                value={info.description}
                onChange={e => setInfo(p => ({ ...p, description: e.target.value }))}
                rows={2}
                placeholder="Descrizione opzionale..."
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm resize-none"
              />
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Frequenza</label>
                <select
                  value={info.frequency}
                  onChange={e => setInfo(p => ({ ...p, frequency: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                >
                  {Object.entries(FREQ_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Sito</label>
                <select
                  value={info.siteId}
                  onChange={e => setInfo(p => ({ ...p, siteId: e.target.value }))}
                  disabled={!info.customerId}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm disabled:opacity-50"
                >
                  <option value="">— tutti i siti —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Punti di controllo */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">
              Punti di Controllo ({points.length})
            </h3>

            {/* Lista punti aggiunti */}
            {points.length > 0 && (
              <ul className="mb-3 space-y-2">
                {points.map((p, idx) => {
                  const a = assets.find(a => a.id === p.assetId)
                  return (
                    <li key={idx} className="flex items-center gap-3 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] px-3 py-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--fc-foreground)] truncate">{p.title}</p>
                        <p className="text-xs text-[var(--fc-foreground-muted)] truncate">
                          {a ? a.name : 'Nessun dispositivo'}{p.requiresPhoto ? ' · Foto se NC' : ''}
                        </p>
                      </div>
                      <button onClick={() => setPoints(prev => prev.filter((_, i) => i !== idx))}
                        className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            {/* Form nuovo punto */}
            <div className="rounded-xl border border-[var(--fc-border)] p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">
                    Punto di controllo (dispositivo)
                  </label>
                  <select
                    value={draft.assetId}
                    onChange={e => setDraft(p => ({ ...p, assetId: e.target.value }))}
                    disabled={!info.customerId}
                    className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm disabled:opacity-50"
                  >
                    <option value="">
                      {info.customerId ? '— seleziona dispositivo —' : '— seleziona prima il cliente —'}
                    </option>
                    {assets.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.assetType ? ` (${a.assetType})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">
                    Nome Controllo *
                  </label>
                  <input
                    value={draft.title}
                    onChange={e => setDraft(p => ({ ...p, title: e.target.value }))}
                    placeholder="Es: Verifica pressione circuito"
                    className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Descrizione</label>
                  <input
                    value={draft.description}
                    onChange={e => setDraft(p => ({ ...p, description: e.target.value }))}
                    placeholder="Opzionale"
                    className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                  />
                </div>
                {/* Foto se NC toggle */}
                <button
                  type="button"
                  onClick={() => setDraft(p => ({ ...p, requiresPhoto: !p.requiresPhoto }))}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                    draft.requiresPhoto
                      ? 'border-[var(--fc-primary)] bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)] text-[var(--fc-primary)]'
                      : 'border-[var(--fc-border)] text-[var(--fc-foreground-muted)]'
                  }`}
                >
                  {draft.requiresPhoto
                    ? <ToggleRight className="h-4 w-4" />
                    : <ToggleLeft className="h-4 w-4" />}
                  Foto se NC
                </button>
                <button
                  onClick={addPoint}
                  disabled={!draft.title}
                  className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 whitespace-nowrap"
                >
                  <Plus className="h-4 w-4" /> Aggiungi
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[var(--fc-border)] px-6 py-4">
          {createMutation.isError && (
            <p className="text-sm text-red-600 mr-auto">Errore durante il salvataggio.</p>
          )}
          <button onClick={onClose} className="rounded-lg border border-[var(--fc-border)] px-4 py-2 text-sm">
            Annulla
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!info.name || createMutation.isPending}
            className="rounded-lg bg-[var(--fc-primary)] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {createMutation.isPending ? 'Salvataggio...' : 'Crea Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Page ----
export function ChecklistsPage() {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['checklist-templates'],
    queryFn: async () => (await api.get<Template[]>('/checklist-templates')).data,
  })

  return (
    <div className="space-y-6">
      {showModal && <NewTemplateModal onClose={() => setShowModal(false)} />}

      <PageHeader
        title="Checklist manutenzione"
        subtitle="Template riutilizzabili per interventi strutturati"
        actions={
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--fc-primary-hover)]"
          >
            <Plus className="h-4 w-4" /> Nuovo template
          </button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-[var(--fc-foreground-muted)]">Caricamento...</p>
      ) : templates.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center py-12 text-center">
            <ClipboardList className="h-12 w-12 text-[var(--fc-foreground-muted)] mb-4" />
            <p className="text-base font-medium text-[var(--fc-foreground)]">Nessun template creato</p>
            <p className="text-sm text-[var(--fc-foreground-muted)] mt-1">
              Crea il primo template per il parco macchine di un cliente.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white"
            >
              <Plus className="h-4 w-4" /> Nuovo template
            </button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map(t => (
            <Card
              key={t.id}
              className="cursor-pointer hover:border-[var(--fc-primary)]"
              onClick={() => navigate(`/maintenance/checklists/${t.id}`)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[var(--fc-foreground)]">{t.name}</p>
                  {t.description && (
                    <p className="text-sm text-[var(--fc-foreground-muted)] mt-0.5">{t.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="inline-flex items-center rounded-full bg-[var(--fc-surface-overlay)] px-2 py-0.5 text-xs text-[var(--fc-foreground-muted)]">
                      {FREQ_LABELS[t.frequency] ?? t.frequency}
                    </span>
                    <span className="text-xs text-[var(--fc-foreground-muted)]">
                      {t.operations?.length ?? 0} punti di controllo
                    </span>
                    <span className="text-xs text-[var(--fc-foreground-muted)]">
                      Creato {formatDate(t.createdAt, 'dd/MM/yyyy')}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-[var(--fc-foreground-muted)]" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
